/* eslint-disable react/no-array-index-key */
// Disabling array-index-key: arrays in this component (warnings, macros, pixels, etc.)
// are dynamically generated from API responses without stable IDs

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef,useState } from 'react';

import { useSnackBar } from '../../hooks/useSnackBar';
import { Asset, CreativeVariable, LivePreviewEligibility, LocationVariable, ProcessedCampaignUrl } from '../../types/previewStudio';
import { DetectedScript, detectScriptsInAdm, detectThirdPartyScripts, detectVendorFromUrl } from '../../util/thirdPartyScriptDetection';

// Normalized metadata shape
export interface NormalizedMetadata {
  warnings: string[];
  macros_replaced: Array<{ macro: string; value: string }>;
  macros_unresolved?: string[];
  macro_coverage?: { replaced: number; unresolved: number };
  // Unified assets array from backend
  assets?: Asset[];
  // Stripped assets when fire_tracking=false (live preview)
  stripped_assets?: Asset[];
  settings_applied?: {
    location?: {
      name?: string;
      lat?: number;
      lon?: number;
      address?: string;
    };
    device?: {
      type?: string;
      os?: string;
    };
  };
  adm?: {
    source?: 'bid_response' | 'flattened' | 'none';
    length?: number;
    contains_script?: boolean;
    contains_img?: boolean;
    contains_iframe?: boolean;
  };
  processed_tag?: string;
  privacy?: {
    gdpr?: 0 | 1;
    gdpr_consent?: string | null;
    us_privacy?: string | null;
    coppa?: 0 | 1;
  };
  health?: 'ok' | 'warning' | 'error';
  summary?: string;
  creative_type?: string;
  vendor?: string;
  processed_vendor?: string;
  size?: string;
  // Variable replacement details
  creative_variables?: CreativeVariable[];
  location_variables?: LocationVariable[];
  processed_campaign_urls?: ProcessedCampaignUrl[];
}

export type PanelKey =
  | 'settings'
  | 'warnings'
  | 'variables'
  | 'processed_tag'
  | 'verification_assets'
  | 'impression_pixels'
  | 'sync_pixels'
  | 'click_trackers'
  | 'third_party_scripts'
  | 'stripped_assets'
  | 'privacy';

// Extracted outside main component to avoid react/no-unstable-nested-components
interface SectionHeaderProps {
  label: string;
  count?: number;
  onCopyAll?: () => void;
  copyAllDisabled?: boolean;
}

function SectionHeader({
  label,
  count,
  onCopyAll,
  copyAllDisabled,
}: SectionHeaderProps) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        {typeof count === 'number' && (
          <Badge
            color={count > 0 ? 'primary' : 'default'}
            badgeContent={count}
            sx={{
              '& .MuiBadge-badge': {
                right: -2,
                top: -2,
              },
            }}
          />
        )}
        {onCopyAll && count !== undefined && count > 0 && (
          <Tooltip title="Copy all">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onCopyAll();
              }}
              disabled={copyAllDisabled}
              sx={{ p: 0.5 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}

/**
 * Truncate a long URL for display, showing domain + abbreviated path
 * Full URL available via tooltip and copy button
 */
function truncateUrl(url: string, maxLength: number = 80): string {
  if (url.length <= maxLength) return url;

  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    const path = urlObj.pathname + urlObj.search;

    // Show domain + first part of path + ... + last part
    const availableForPath = maxLength - domain.length - 10; // 10 for "https://" and "..."
    if (availableForPath > 20) {
      const halfPath = Math.floor(availableForPath / 2);
      const truncatedPath = path.length > availableForPath
        ? `${path.slice(0, halfPath)}...${path.slice(-halfPath)}`
        : path;
      return `${domain}${truncatedPath}`;
    }
    // Fallback: just truncate the whole thing
    return `${url.slice(0, maxLength - 3)}...`;
  } catch {
    // If URL parsing fails, just truncate
    return `${url.slice(0, maxLength - 3)}...`;
  }
}

interface DebugAccordionPanelProps {
  metadata: NormalizedMetadata;
  previewId?: string | number;
  previewMode?: 'simulated' | 'live';
  liveEligibility?: LivePreviewEligibility | null;
  checkingEligibility?: boolean;
  eligibilityError?: string | null;
  onRefreshEligibility?: () => void;
  fireTracking?: boolean;
  iframeRef?: React.RefObject<HTMLIFrameElement | null>;
}

export default function DebugAccordionPanel({
  metadata,
  previewId,
  previewMode = 'simulated',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  liveEligibility,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  checkingEligibility,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  eligibilityError,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onRefreshEligibility,
  fireTracking = false,
  iframeRef,
}: DebugAccordionPanelProps) {
  const { showSnackBar } = useSnackBar();
  const [expanded, setExpanded] = useState<PanelKey | false>('settings');
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const previousPreviewIdRef = useRef<string | number | undefined>(previewId);

  // Normalize arrays
  const warnings = metadata.warnings ?? [];
  const macros = metadata.macros_replaced ?? [];
  const macrosUnresolved = metadata.macros_unresolved ?? [];
  const strippedAssets = metadata.stripped_assets ?? [];
  const creativeVariables = metadata.creative_variables ?? [];
  const locationVariables = metadata.location_variables ?? [];
  const processedCampaignUrls = metadata.processed_campaign_urls ?? [];

  // Check for URLs with missing variables (common spend issue)
  const urlsWithMissingVars = processedCampaignUrls.filter(
    (url) => url.missing_variables && url.missing_variables.length > 0
  );
  const hasMissingVariables = urlsWithMissingVars.length > 0;

  // State for embedded (runtime) detected scripts from iframe DOM
  const [embeddedScripts, setEmbeddedScripts] = useState<DetectedScript[]>([]);

  // Scan iframe DOM for dynamically-loaded scripts after preview renders
  useEffect(() => {
    const scanIframe = () => {
      try {
        const iframe = iframeRef?.current;
        if (!iframe) return;

        // Try both access methods (contentDocument or contentWindow.document)
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) return;

        const results: DetectedScript[] = [];

        // Recursive function to scan a document and its nested iframes
        const scanDocument = (doc: Document) => {
          // Scan all script elements
          const scripts = doc.getElementsByTagName('script');
          Array.from(scripts).forEach((script) => {
            const src = script.getAttribute('src') || '';
            const content = script.textContent || '';
            if (src) {
              results.push(...detectThirdPartyScripts(src, 'iframe'));
            }
            if (content) {
              results.push(...detectThirdPartyScripts(content, 'iframe'));
            }
          });

          // Scan all img elements (verification pixels)
          const imgs = doc.getElementsByTagName('img');
          Array.from(imgs).forEach((img) => {
            const src = img.getAttribute('src') || '';
            if (src) {
              results.push(...detectThirdPartyScripts(src, 'iframe'));
            }
          });

          // Recursively scan nested iframes (DCM often creates these)
          const nestedIframes = doc.getElementsByTagName('iframe');
          Array.from(nestedIframes).forEach((nestedIframe) => {
            try {
              const nestedDoc = nestedIframe.contentDocument || nestedIframe.contentWindow?.document;
              if (nestedDoc) {
                scanDocument(nestedDoc);
              }
            } catch {
              // Cross-origin nested iframe - skip
            }
          });
        };

        scanDocument(iframeDoc);

        // Deduplicate by vendor
        const unique = results.filter(
          (r, i, self) => i === self.findIndex((t) => t.vendor === r.vendor)
        );

        setEmbeddedScripts(unique);
      } catch {
        // Cross-origin or other access error - silently ignore
      }
    };

    // Reset on new preview
    setEmbeddedScripts([]);

    // Two scans: 1s for quick scripts, 3s for slower DCM/verification pixels
    const timeoutId1 = setTimeout(scanIframe, 1000);
    const timeoutId2 = setTimeout(scanIframe, 3000);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
    };
  }, [iframeRef, previewId]);

  // Group assets by purpose for display, supplemented by frontend detection
  const assetsByPurpose = useMemo(() => {
    const assets = metadata.assets ?? [];
    const grouped: Record<string, Asset[]> = {};

    // First, group backend assets, augmenting missing vendor_hint with frontend detection
    assets.forEach((asset) => {
      const purpose = asset.purpose || 'unknown';
      if (!grouped[purpose]) {
        grouped[purpose] = [];
      }
      // Augment missing vendor_hint using frontend detection
      const augmentedAsset = { ...asset };
      if (!augmentedAsset.vendor_hint && augmentedAsset.url) {
        const detectedVendor = detectVendorFromUrl(augmentedAsset.url);
        if (detectedVendor) {
          augmentedAsset.vendor_hint = detectedVendor;
        }
      }
      grouped[purpose].push(augmentedAsset);
    });

    // Get existing vendor hints from backend assets for deduplication
    const existingVendors = new Set(
      assets
        .filter((a) => a.vendor_hint)
        .map((a) => a.vendor_hint!.toLowerCase())
    );

    // Track what we've added to avoid duplicates between static and embedded
    const addedVendors = new Set<string>();

    // Run frontend detection on processed_tag (static/in-markup detection)
    if (metadata.processed_tag) {
      const detectedScripts = detectScriptsInAdm(metadata.processed_tag);

      detectedScripts.forEach((detected) => {
        const vendorLower = detected.vendor.toLowerCase();
        const alreadyExists = existingVendors.has(vendorLower) ||
          assets.some((a) => detected.url && a.url.includes(detected.url));

        if (!alreadyExists && detected.url) {
          if (!grouped.verification) {
            grouped.verification = [];
          }
          grouped.verification.push({
            type: 'img',
            url: detected.url,
            purpose: 'verification',
            vendor_hint: `${detected.vendor} (in markup)`,
          });
          addedVendors.add(vendorLower);
        }
      });
    }

    // Add embedded (runtime) detected scripts from iframe DOM
    embeddedScripts.forEach((detected) => {
      const vendorLower = detected.vendor.toLowerCase();
      const alreadyExists = existingVendors.has(vendorLower) ||
        addedVendors.has(vendorLower) ||
        assets.some((a) => detected.url && a.url.includes(detected.url));

      if (!alreadyExists && detected.url) {
        if (!grouped.verification) {
          grouped.verification = [];
        }
        grouped.verification.push({
          type: 'img',
          url: detected.url,
          purpose: 'verification',
          vendor_hint: `${detected.vendor} (embedded)`,
        });
      }
    });

    return grouped;
  }, [metadata.assets, metadata.processed_tag, embeddedScripts]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    showSnackBar('Copied to clipboard', 'success', { event: 'preview_copied' });
  };

  type Copyable = string | { url: string };
  const handleCopyAll = (items: Copyable[]) => {
    const text = items
      .map((item) => (typeof item === 'string' ? item : item.url))
      .join('\n');
    navigator.clipboard.writeText(text);
    showSnackBar(`Copied ${items.length} item(s) to clipboard`, 'success', { event: 'preview_copied_all', count: items.length });
  };

  // Opens Settings by default
  const defaultExpanded = useMemo<PanelKey>(() => 'settings', []);

  useEffect(() => {
    if (previewId !== undefined && previewId !== previousPreviewIdRef.current) {
      setHasUserInteracted(false);
      previousPreviewIdRef.current = previewId;
    }
  }, [previewId]);

  useEffect(() => {
    if (!hasUserInteracted) {
      setExpanded(defaultExpanded);
    }
  }, [defaultExpanded, hasUserInteracted]);

  const onToggle = (key: PanelKey) => (_: unknown, isExpanded: boolean) => {
    setHasUserInteracted(true);
    setExpanded(isExpanded ? key : false);
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 1 }}>
      {/* Settings */}
      <Accordion expanded={expanded === 'settings'} onChange={onToggle('settings')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SectionHeader label="Settings Applied" />
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Creative Info */}
            {(metadata.creative_type || metadata.size || metadata.vendor || metadata.processed_vendor) && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Creative Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {metadata.creative_type && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Type:
                      </Typography>
                      <Chip
                        label={metadata.creative_type.toUpperCase()}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  )}
                  {metadata.size && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Size:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {metadata.size}
                      </Typography>
                    </Box>
                  )}
                  {metadata.vendor && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Vendor:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {metadata.vendor}
                      </Typography>
                    </Box>
                  )}
                  {metadata.processed_vendor && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Processed Vendor:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {metadata.processed_vendor}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Location Settings */}
            {metadata.settings_applied?.location && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Location Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {metadata.settings_applied.location.name && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Name:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {metadata.settings_applied.location.name}
                      </Typography>
                    </Box>
                  )}
                  {metadata.settings_applied.location.lat !== undefined &&
                    metadata.settings_applied.location.lon !== undefined && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Coordinates:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                        >
                          {metadata.settings_applied.location.lat.toFixed(6)},{' '}
                          {metadata.settings_applied.location.lon.toFixed(6)}
                        </Typography>
                      </Box>
                    )}
                  {metadata.settings_applied.location.address && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Address:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, textAlign: 'right', maxWidth: 200 }}
                      >
                        {metadata.settings_applied.location.address}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Device Settings */}
            {metadata.settings_applied?.device && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Device Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {metadata.settings_applied.device.type && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Type:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                      >
                        {metadata.settings_applied.device.type}
                      </Typography>
                    </Box>
                  )}
                  {metadata.settings_applied.device.os && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        OS:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, textTransform: 'uppercase' }}
                      >
                        {metadata.settings_applied.device.os}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Warnings */}
      <Accordion expanded={expanded === 'warnings'} onChange={onToggle('warnings')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SectionHeader label="Warnings" count={warnings.length} />
        </AccordionSummary>
        <AccordionDetails>
          {warnings.length ? (
            <Stack spacing={1}>
              {warnings.map((warning, idx) => (
                <Alert key={idx} severity="warning">
                  {warning}
                </Alert>
              ))}
            </Stack>
          ) : (
            <Alert severity="success" icon={false}>
              <Typography variant="body2">No warnings - preview generated successfully</Typography>
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Variables - Combined section for all macro/variable replacements */}
      <Accordion expanded={expanded === 'variables'} onChange={onToggle('variables')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SectionHeader
            label="Variables"
            count={creativeVariables.length + locationVariables.length + processedCampaignUrls.length + macros.length}
          />
          {(hasMissingVariables || macrosUnresolved.length > 0) && (
            <Chip
              label={hasMissingVariables ? 'Missing Variables' : 'Unresolved'}
              size="small"
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Missing Variables Warning */}
            {hasMissingVariables && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Missing Variables Detected
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                  This is a common cause of campaigns not spending. The following URLs reference variables that are not set:
                </Typography>
                {urlsWithMissingVars.map((url, idx) => (
                  <Typography key={idx} variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                    {url.macro}: missing {url.missing_variables?.join(', ')}
                  </Typography>
                ))}
              </Alert>
            )}

            {/* Unresolved Macros Warning */}
            {macrosUnresolved.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Unresolved Macros ({macrosUnresolved.length})
                </Typography>
                <Stack spacing={0.5}>
                  {macrosUnresolved.map((macro, idx) => (
                    <Typography key={idx} variant="caption" fontFamily="monospace">
                      {macro}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}

            {/* Creative Variables */}
            {creativeVariables.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Creative Variables ({creativeVariables.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Macro</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Value</TableCell>
                        <TableCell sx={{ width: 40 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {creativeVariables.map((variable, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                            %{`{${variable.macro}}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.7rem',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Tooltip title={variable.value} placement="top">
                              <span>{variable.value}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title="Copy value">
                              <IconButton size="small" onClick={() => handleCopy(variable.value)}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Location Variables */}
            {locationVariables.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Location Variables ({locationVariables.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Macro</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Value</TableCell>
                        <TableCell sx={{ width: 40 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locationVariables.map((variable, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                            %{`{${variable.macro}}`}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.7rem',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Tooltip title={variable.value} placement="top">
                              <span>{variable.value}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title="Copy value">
                              <IconButton size="small" onClick={() => handleCopy(variable.value)}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

              {/* Campaign URLs */}
              {processedCampaignUrls.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Campaign URLs ({processedCampaignUrls.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Macro</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Value</TableCell>
                          <TableCell sx={{ width: 80 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {processedCampaignUrls.map((url, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                              {url.macro}
                              {url.missing_variables && url.missing_variables.length > 0 && (
                                <Tooltip title={`Missing: ${url.missing_variables.join(', ')}`}>
                                  <Chip
                                    label="!"
                                    size="small"
                                    color="warning"
                                    sx={{ ml: 1, minWidth: 20, height: 20 }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={url.processed} placement="top-start">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.7rem',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 200,
                                    cursor: 'help',
                                  }}
                                >
                                  {url.processed}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title="Copy URL">
                                <IconButton size="small" onClick={() => handleCopy(url.processed)}>
                                  <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open in new tab">
                                <IconButton size="small" onClick={() => window.open(url.processed, '_blank')}>
                                  <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Other Replaced Macros (legacy macros_replaced not covered by structured fields) */}
              {macros.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Other Macros ({macros.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Macro</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Value</TableCell>
                          <TableCell sx={{ width: 40 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {macros.map((macro, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                              {macro.macro}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.7rem',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Tooltip title={macro.value} placement="top">
                                <span>{macro.value}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title="Copy value">
                                <IconButton size="small" onClick={() => handleCopy(macro.value)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Empty state */}
              {creativeVariables.length === 0 && locationVariables.length === 0 && processedCampaignUrls.length === 0 && macros.length === 0 && macrosUnresolved.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No variable substitutions in this preview.
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

      {/* Assets grouped by purpose */}
      {/* Verification Assets */}
      {assetsByPurpose.verification && assetsByPurpose.verification.length > 0 && (
        <Accordion expanded={expanded === 'verification_assets'} onChange={onToggle('verification_assets')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader
              label="Verification Assets"
              count={assetsByPurpose.verification.length}
              onCopyAll={() => handleCopyAll(assetsByPurpose.verification.map((a) => a.url))}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              {assetsByPurpose.verification.map((asset, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                    <Chip
                      label={asset.type}
                      size="small"
                      color={(() => {
                        if (asset.type === 'script') return 'primary';
                        if (asset.type === 'img') return 'default';
                        return 'secondary';
                      })()}
                    />
                    {asset.vendor_hint && (
                      <Chip label={asset.vendor_hint} size="small" variant="outlined" color="info" />
                    )}
                  </Stack>
                  <Tooltip title={asset.url} placement="top-start">
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        display: 'block',
                        mb: 1,
                        cursor: 'help',
                      }}
                    >
                      {truncateUrl(asset.url)}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Copy URL">
                      <IconButton size="small" onClick={() => handleCopy(asset.url)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open in new tab">
                      <IconButton size="small" onClick={() => window.open(asset.url, '_blank')}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Impression Pixels */}
      {assetsByPurpose.impression && assetsByPurpose.impression.length > 0 && (
        <Accordion expanded={expanded === 'impression_pixels'} onChange={onToggle('impression_pixels')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader
              label="Impression Pixels"
              count={assetsByPurpose.impression.length}
              onCopyAll={() => handleCopyAll(assetsByPurpose.impression.map((a) => a.url))}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              {assetsByPurpose.impression.map((asset, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                    {asset.vendor_hint && (
                      <Chip label={asset.vendor_hint} size="small" variant="outlined" color="info" />
                    )}
                  </Stack>
                  <Tooltip title={asset.url} placement="top-start">
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        display: 'block',
                        mb: 1,
                        cursor: 'help',
                      }}
                    >
                      {truncateUrl(asset.url)}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Copy URL">
                      <IconButton size="small" onClick={() => handleCopy(asset.url)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open in new tab">
                      <IconButton size="small" onClick={() => window.open(asset.url, '_blank')}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Sync Pixels */}
      {assetsByPurpose.sync && assetsByPurpose.sync.length > 0 && (
        <Accordion expanded={expanded === 'sync_pixels'} onChange={onToggle('sync_pixels')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader
              label="Sync Pixels"
              count={assetsByPurpose.sync.length}
              onCopyAll={() => handleCopyAll(assetsByPurpose.sync.map((a) => a.url))}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              {assetsByPurpose.sync.map((asset, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                    {asset.vendor_hint && (
                      <Chip label={asset.vendor_hint} size="small" variant="outlined" color="info" />
                    )}
                  </Stack>
                  <Tooltip title={asset.url} placement="top-start">
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        display: 'block',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'help',
                      }}
                    >
                      {truncateUrl(asset.url)}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Copy URL">
                      <IconButton size="small" onClick={() => handleCopy(asset.url)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open in new tab">
                      <IconButton size="small" onClick={() => window.open(asset.url, '_blank')}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Click Trackers */}
      {assetsByPurpose.click && assetsByPurpose.click.length > 0 && (
        <Accordion expanded={expanded === 'click_trackers'} onChange={onToggle('click_trackers')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader
              label="Click Trackers"
              count={assetsByPurpose.click.length}
              onCopyAll={() => handleCopyAll(assetsByPurpose.click.map((a) => a.url))}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              {assetsByPurpose.click.map((asset, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
                  <Tooltip title={asset.url} placement="top-start">
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        display: 'block',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'help',
                      }}
                    >
                      {truncateUrl(asset.url)}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Copy URL">
                      <IconButton size="small" onClick={() => handleCopy(asset.url)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open in new tab">
                      <IconButton size="small" onClick={() => window.open(asset.url, '_blank')}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Third-Party Scripts */}
      {assetsByPurpose.third_party && assetsByPurpose.third_party.length > 0 && (
        <Accordion expanded={expanded === 'third_party_scripts'} onChange={onToggle('third_party_scripts')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader
              label="Third-Party Scripts"
              count={assetsByPurpose.third_party.length}
              onCopyAll={() => handleCopyAll(assetsByPurpose.third_party.map((a) => a.url))}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              {assetsByPurpose.third_party.map((asset, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                    {asset.vendor_hint && (
                      <Chip label={asset.vendor_hint} size="small" variant="outlined" color="info" />
                    )}
                  </Stack>
                  <Tooltip title={asset.url} placement="top-start">
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        display: 'block',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'help',
                      }}
                    >
                      {truncateUrl(asset.url)}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Copy URL">
                      <IconButton size="small" onClick={() => handleCopy(asset.url)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open in new tab">
                      <IconButton size="small" onClick={() => window.open(asset.url, '_blank')}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Stripped Assets (removed during safe preview) */}
      {!fireTracking && strippedAssets.length > 0 && (
        <Accordion
          expanded={expanded === 'stripped_assets'}
          onChange={onToggle('stripped_assets')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader
              label="Stripped for Preview"
              count={strippedAssets.length}
              onCopyAll={() => handleCopyAll(strippedAssets.map((a) => a.url))}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="caption">
                  These assets were removed because tracking is disabled (preview-safe mode). They will fire in production.
                </Typography>
              </Alert>
              <Stack spacing={1.5}>
                {strippedAssets.map((asset, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', opacity: 0.7, overflow: 'hidden' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                      <Chip
                        label={asset.type}
                        size="small"
                        color={(() => {
                          if (asset.type === 'script') return 'primary';
                          if (asset.type === 'iframe') return 'secondary';
                          return 'default';
                        })()}
                      />
                      {asset.purpose && (
                        <Chip
                          label={asset.purpose}
                          size="small"
                          variant="outlined"
                          color="warning"
                        />
                      )}
                      {asset.vendor_hint && (
                        <Chip
                          label={asset.vendor_hint}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      )}
                    </Stack>
                    <Tooltip title={asset.url} placement="top-start">
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          display: 'block',
                          mb: 1,
                          textDecoration: 'line-through',
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'help',
                        }}
                      >
                        {truncateUrl(asset.url)}
                      </Typography>
                    </Tooltip>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Copy URL">
                        <IconButton size="small" onClick={() => handleCopy(asset.url)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open in new tab">
                        <IconButton
                          size="small"
                          onClick={() => window.open(asset.url, '_blank')}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Processed Tag */}
      {metadata.processed_tag && (
        <Accordion expanded={expanded === 'processed_tag'} onChange={onToggle('processed_tag')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader
              label="Processed Tag"
              onCopyAll={() => handleCopy(metadata.processed_tag!)}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                  Final HTML after all variable substitutions. This is what will be sent to the ad server.
                </Typography>
                {/* Show tracking status in live mode */}
                {previewMode === 'live' && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: fireTracking ? 'warning.main' : 'info.main', 
                      display: 'block', 
                      mb: 1,
                      fontSize: '0.7rem'
                    }}
                  >
                    {fireTracking 
                      ? 'Tracking enabled (will count metrics).' 
                      : 'Tracking disabled (preview-safe).'}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Length: {metadata.processed_tag.length} characters
                  </Typography>
                  <Tooltip title="Copy processed tag">
                    <IconButton size="small" onClick={() => handleCopy(metadata.processed_tag!)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  maxHeight: 400,
                  overflow: 'auto',
                  position: 'relative',
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.5,
                  }}
                >
                  {metadata.processed_tag}
                </pre>
              </Paper>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Privacy */}
      {metadata.privacy && (
        <Accordion expanded={expanded === 'privacy'} onChange={onToggle('privacy')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader label="Privacy Settings" />
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {metadata.privacy.gdpr !== undefined && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    GDPR:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {metadata.privacy.gdpr}
                  </Typography>
                </Box>
              )}
              {metadata.privacy.gdpr_consent !== undefined && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    GDPR Consent:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 500, fontFamily: 'monospace', maxWidth: 200 }}
                  >
                    {metadata.privacy.gdpr_consent || 'null'}
                  </Typography>
                </Box>
              )}
              {metadata.privacy.us_privacy !== undefined && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    US Privacy:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 500, fontFamily: 'monospace', maxWidth: 200 }}
                  >
                    {metadata.privacy.us_privacy || 'null'}
                  </Typography>
                </Box>
              )}
              {metadata.privacy.coppa !== undefined && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    COPPA:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {metadata.privacy.coppa}
                  </Typography>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

